import React, { useState } from 'react';
import * as yup from 'yup';
import OverlayLoading from '../../../../../../Components/OverlayLoading';
import Form from '../../../../../../Components/Form/Form';
import InputField from '../../../../../../Components/Form/components/InputField';
import SubmitButton from '../../../../../../Components/Button/SubmitButton';
import withForm from '../../../../../../Components/Form/withForm';
import { NCZIPreferredHealthProfessional } from '../../../../Interfaces/ncziPatientInformations';
import NCZICommandResult from '../../../NCZICommandResult/NCZICommandResult';
import NCZIAddress, {
	fillAddressDefaultValues,
	NCZIAddressValidator,
} from '../../../NCZIAddress';
import { NCZI_OID } from '../../../../Enums/ncziOid.enum';
import { NCZIReferenceSelectItem } from '../../../ReferenceSearchSelect/NCZIReferenceSearchSelect';
import { WritePatientSummaryContactInformation } from '../../../../Commands/WritePatientSummaryContactInformation';
import { useLazyNcziExtensionItemQuery } from '../../../../../../graphql/graphql';
import { useEffectAsync } from '../../../../../../helpers';
import useNCZICommandProcess from '../../../../Hooks/useNCZICommandProcess';
import NCZIExtensionSearchSelectField from '../../../ExtensionSearchSelect/NCZIExtensionSearchSelectField';
import {
	NCZIExtensionSelectItem,
	NCZIExtensionValidator,
} from '../../../ExtensionSearchSelect/NCZIExtensionSearchSelect';
import useNCZIDefaultValues from '../../../../Hooks/useNCZIDefaultValues';
import InputWrapper from '../../../../../../Components/Form/components/InputWrapper/InputWrapper';

export interface NCZIEditPreferredHealthProfessionalFormValues {
	firstName: string;
	lastName: string;
	note: string;
	healthProfessionalId: NCZIExtensionSelectItem;
	address: {
		street: string;
		descriptiveNumber: string;
		city: NCZIReferenceSelectItem;
		country: NCZIReferenceSelectItem;
		postCode: NCZIReferenceSelectItem;
		region: NCZIReferenceSelectItem;
	};
}

interface Props {
	onClose?: (needReload: boolean) => void;
	preferredHealthProfessional?: NCZIPreferredHealthProfessional;
	patientIdentifier: string;
}

const NCZIEditPreferredHealthProfessionalForm: React.FC<Props> = props => {
	const { onClose, patientIdentifier, preferredHealthProfessional } = props;
	const [defaultValues, setDefaultValues] = useState({});
	const [isLoading, setIsLoading] = useState(true);
	const [getExtensionItem] = useLazyNcziExtensionItemQuery();
	const { extensionDefaultValue } = useNCZIDefaultValues();
	const { processingCommandId, processCommand, isProcessing } =
		useNCZICommandProcess({
			onFinish: () => onClose?.(true),
		});

	const handleSubmit = async (
		values: NCZIEditPreferredHealthProfessionalFormValues,
	) => {
		const finalValues = {
			healthProfessionalId: values.healthProfessionalId.value,
			firstName: values.firstName,
			lastName: values.lastName,
			note: values.note,
			address: values.address.street
				? {
						street: values.address.street,
						descriptiveNumber: values.address.descriptiveNumber,
						city: values.address.city.value,
						country: values.address.country.value,
						postCode: values.address.postCode.value,
						region: values.address.region.value,
						district: undefined,
						guideNumber: undefined,
				  }
				: undefined,
		};

		const writeCommand = WritePatientSummaryContactInformation({
			patientCode: patientIdentifier,
			preferredHealthProfessionals: [
				{
					...finalValues,
					recordId: preferredHealthProfessional
						? preferredHealthProfessional.rcId
						: undefined,
				},
			],
		});

		await processCommand(writeCommand);
	};

	const validationSchema = yup.object({
		healthProfessionalId: NCZIExtensionValidator(
			'nczi.preferredHealthProfessional.healthProfessional',
		),
		firstName: yup.string().required('base.shared.firstName'),
		lastName: yup.string().required('base.shared.lastName'),
		note: yup.string().max(4000, 'nczi.validation.max4000').nullable(),
		address: NCZIAddressValidator,
	});

	useEffectAsync(async () => {
		if (preferredHealthProfessional) {
			setDefaultValues({
				healthProfessionalId: await extensionDefaultValue(
					preferredHealthProfessional.id,
				),
				firstName: preferredHealthProfessional?.firstName,
				lastName: preferredHealthProfessional?.lastName,
				note: preferredHealthProfessional?.note,
				address: fillAddressDefaultValues(preferredHealthProfessional?.address),
			});
		}

		setIsLoading(false);
	}, []);

	if (isProcessing && processingCommandId) {
		return <NCZICommandResult commandId={processingCommandId} />;
	}

	return (
		<div>
			{isLoading && <OverlayLoading />}
			{!isLoading && (
				<Form defaultValues={defaultValues} schema={validationSchema}>
					<InputWrapper>
						<NCZIExtensionSearchSelectField
							name='healthProfessionalId'
							oid={NCZI_OID.HEALTH_PROFESSIONAL}
							label='nczi.preferredHealthProfessional.healthProfessional'
							isRequired
							showCode
						/>
					</InputWrapper>
					<div className='row align-items-end'>
						<div className='col-md-6'>
							<InputWrapper>
								<InputField
									label='base.shared.firstName'
									name='firstName'
									isRequired
								/>
							</InputWrapper>
						</div>
						<div className='col-md-6'>
							<InputWrapper>
								<InputField
									label='base.shared.lastName'
									name='lastName'
									isRequired
								/>
							</InputWrapper>
						</div>
					</div>
					<InputWrapper>
						<InputField
							label='base.internalNote'
							name='note'
							isRequired={false}
							charactersLimit={4000}
						/>
					</InputWrapper>
					<NCZIAddress />
					<div className='mt-4'>
						<SubmitButton
							title='component.nczi.setup.save'
							onSubmit={handleSubmit}
						/>
					</div>
				</Form>
			)}
		</div>
	);
};

export default withForm(NCZIEditPreferredHealthProfessionalForm);
